// Web v3 copy, canonical comp updated October 2, 2026.
// Outcome claims remain gated by existing authoritative state.
// Spanish: the Founder's full review of 2026-10-06, applied by scripts/apply-spanish-review.js.

export const en = {
  "authAction.title.resetPassword": "Reset your password",
  "authAction.title.verifyEmail": "Verify your email",
  "authAction.title.recoverEmail": "Restore your email",
  "authAction.title.unsupported": "Account action",

  "authAction.checking": "Checking your link",

  "authAction.form.intro": "{email}",
  "authAction.form.newPassword": "New password",
  "authAction.form.newPassword.placeholder": "Your new password",
  "authAction.form.confirmPassword": "Confirm new password",
  "authAction.form.confirmPassword.placeholder": "The same password",
  "authAction.form.minLength": "At least {count} characters.",
  "authAction.form.submit": "Save password",
  "authAction.form.submit.busy": "Saving…",
  "authAction.form.mismatch": "Passwords don’t match.",

  "authAction.error.hint":
    "",
  "authAction.error.newLink": "Ask for a new link",

  "authAction.expired.verify.line": "Request a new one from the Lura app.",
  "authAction.expired.recover.line":
    "Contact support to restore your email.",
  "authAction.expired.recover.action": "Get help",

  "authAction.done.reset": "Password updated",
  "authAction.done.reset.next": "You can now {signIn}, or return to the Lura app.",
  "authAction.done.reset.signInLink": "sign in",
  "authAction.done.recover": "Email restored",
  "authAction.done.recoverTo": "Your sign-in email is back to {email}.",
  "authAction.done.recover.hint":
    "Reset your password if you didn’t make this change.",
  "authAction.done.verify.close": "Return to your booking or account.",
  "authAction.error.backToSignIn": "Back to sign in",
  "authAction.done.verify": "Email verified",

  "authAction.outcome.expired": "This link has expired.",
  "authAction.outcome.invalid":
    "This link is no longer valid.",
  "authAction.outcome.invalid.reset":
    "This link is no longer valid.",
  "authAction.outcome.disabled": "This account has been disabled.",
  "authAction.outcome.notFound":
    "This link isn’t available.",
  "authAction.outcome.notFound.reset": "This link isn’t available.",
  "authAction.outcome.malformed":
    "This link is incomplete.",
  "authAction.outcome.malformed.reset": "This link is incomplete.",
  "authAction.outcome.unusable": "This link isn’t available.",
  "authAction.outcome.unusable.reset": "This link isn’t available.",

  "authAction.outcome.policyPassword":
    "That password doesn’t meet the requirements.",
  "authAction.outcome.weakPassword": "Choose a longer password — at least {count} characters.",

  "authAction.outcome.unverified":
    "We couldn’t check this link.",
  "authAction.outcome.indeterminate":
    "We couldn’t confirm the change.",

  "authAction.outcome.mismatch":
    "This link isn’t available.",
  "authAction.outcome.mismatch.reset":
    "This link isn’t available.",
  "authAction.outcome.unsupported":
    "This link isn’t supported on the web. Open it on a device with the Lura app.",
  "authAction.outcome.incomplete": "This link is incomplete.",
  "authAction.outcome.incomplete.reset": "This link is incomplete.",
} as const;

export type AuthActionKey = keyof typeof en;

export const es: Partial<Record<AuthActionKey, string>> = {
  // These paragraphs are intentionally omitted in both locales; no translated prose.
  "authAction.error.hint": "",
  "authAction.title.resetPassword": "Restablece tu contraseña",
  "authAction.title.verifyEmail": "Verifica tu correo electrónico",
  "authAction.title.recoverEmail": "Recupera tu correo electrónico",
  "authAction.title.unsupported": "Acción de cuenta",
  "authAction.checking": "Verificando tu enlace",
  "authAction.form.newPassword": "Contraseña nueva",
  "authAction.form.newPassword.placeholder": "Tu contraseña nueva",
  "authAction.form.confirmPassword": "Confirma la contraseña nueva",
  "authAction.form.confirmPassword.placeholder": "La misma contraseña",
  "authAction.form.minLength": "Al menos {count} caracteres.",
  "authAction.error.newLink": "Solicitar otro enlace",
  "authAction.done.reset.next": "Ahora puedes {signIn} o volver a la app de Lura.",
  "authAction.done.reset.signInLink": "iniciar sesión",
  "authAction.error.backToSignIn": "Volver a iniciar sesión",
  // The reviewed pair of the identical English sentence, formerly
  // `authAction.outcome.expired.reset`.
  "authAction.outcome.expired": "Este enlace venció.",
  // The reviewed Spanish of the identical sentence, formerly the second half
  // of `authAction.outcome.expired`.
  "authAction.expired.verify.line": "Solicita uno nuevo desde la app de Lura.",
  "authAction.outcome.weakPassword":
    "Elige una contraseña más larga: al menos {count} caracteres.",
  "authAction.outcome.unsupported": "Este enlace no funciona en la web. Ábrelo en un dispositivo con la app de Lura.",
  "authAction.outcome.incomplete.reset": "Este enlace está incompleto.",
  "authAction.expired.recover.action": "Obtener ayuda",
  "authAction.done.recover": "Correo electrónico restaurado",
  "authAction.done.recover.hint": "Restablece tu contraseña si no hiciste este cambio.",
  "authAction.done.recoverTo": "Tu correo para iniciar sesión vuelve a ser {email}.",
  "authAction.done.reset": "Contraseña actualizada",
  "authAction.done.verify": "Correo electrónico verificado",
  "authAction.done.verify.close": "Vuelve a tu cita o a tu cuenta.",
  "authAction.expired.recover.line": "Contacta a soporte para restaurar tu correo electrónico.",
  "authAction.form.intro": "{email}",
  "authAction.form.mismatch": "Las contraseñas no coinciden.",
  "authAction.form.submit": "Guardar contraseña",
  "authAction.form.submit.busy": "Guardando…",
  "authAction.outcome.disabled": "Esta cuenta está deshabilitada.",
  "authAction.outcome.incomplete": "Este enlace está incompleto.",
  "authAction.outcome.indeterminate": "No pudimos confirmar el cambio.",
  "authAction.outcome.invalid": "Este enlace ya no es válido.",
  "authAction.outcome.invalid.reset": "Este enlace ya no es válido.",
  "authAction.outcome.malformed": "Este enlace está incompleto.",
  "authAction.outcome.malformed.reset": "Este enlace está incompleto.",
  "authAction.outcome.mismatch": "Este enlace no está disponible.",
  "authAction.outcome.mismatch.reset": "Este enlace no está disponible.",
  "authAction.outcome.notFound": "Este enlace no está disponible.",
  "authAction.outcome.notFound.reset": "Este enlace no está disponible.",
  "authAction.outcome.policyPassword": "Esa contraseña no cumple con los requisitos.",
  "authAction.outcome.unusable": "Este enlace no está disponible.",
  "authAction.outcome.unusable.reset": "Este enlace no está disponible.",
  "authAction.outcome.unverified": "No pudimos verificar este enlace.",
};
